/**
 * Simple Permission System for Menu Items
 * One function to check all permissions
 */

/**
 * Features exclusive to LICENCIA_PRO customers.
 * LICENCIA_BASICA users will SEE these items in the menu (locked) but cannot access them.
 * Edit this set to control which features require a Pro license upgrade.
 */
const PRO_ONLY_FEATURES = new Set([
  "qrs",
  "lockersale",
  "discount-codes",
  "redeemcodes",
  "wristband-redeemcodes",
  "rentals",
  "calendars",
  "email-templates",
  "partners",
  "integraciones",
  "pro-dashboard",
  "my-cash-users",
  "my-pda-users",
  "my-manager-users",
  "dashboard-rental",
  "localizacion"
]);

/**
 * Check if a feature is locked for the current user.
 * Returns true when a LICENCIA_BASICA customer views a PRO-only feature.
 */
export function isFeatureLocked(role, type, _category, feature) {
  return (
    role === "CUSTOMER" &&
    type === "LICENCIA_BASICA" &&
    PRO_ONLY_FEATURES.has(feature)
  );
}

/**
 * Check if user can access a menu feature
 * @param {string} role - User role (ADMIN, CUSTOMER)
 * @param {string} type - User type (PRODUCCION, PROYECTOS, SAT1, SAT2, PROVEEDOR, CONTABILIDAD, MASTER for ADMIN; GROUP, LICENCIA_BASICA, LICENCIA_PRO, PDA, CASH for CUSTOMER)
 * @param {string} category - User category (not used in this matrix, but kept for compatibility)
 * @param {string} feature - Feature to check
 * @returns {boolean}
 */
export function canAccess(role, type, category, feature) {
  const r = role;
  const t = type;
  const c = category;

  // Quick helpers

  //MAIN ROLES
  const isAdmin = r === "ADMIN";
  const isCustomer = r === "CUSTOMER";

  //CUSTOMER TYPES
  const isGroup = t === "GROUP";
  const isLicenciaBasica = t === "LICENCIA_BASICA";
  const isLicenciaPro = t === "LICENCIA_PRO";
  const isPDA = t === "PDA";
  const isCash = t === "CASH";
  const isManager = t === "MANAGER";

  //CUSTOMER SPECIAL TYPES
  const isBaqueira = c === "BAQUEIRA"; //EXTRA TYPE FOR BAQUEIRA
  const isMontarto = c === "MONTARTO"; //EXTRA TYPE FOR MONTARTO

  //ADMIN TYPES
  const isProduccion = t === "PRODUCCION";
  const isProyectos = t === "PROYECTOS";
  const isSat1 = t === "SAT1";
  const isSat2 = t === "SAT2";
  const isProveedor = t === "PROVEEDOR";
  const isContabilidad = t === "CONTABILIDAD";
  const isProgrammer = t === "PROGRAMMER"; //EXTRA TYPE FOR PROGRAMMERS
  const isMaster = t === "MASTER";
  const isComercial = t === "COMERCIAL";

  // MASTER has access to everything
  if (isMaster) return true;

  // CONTABILIDAD has restricted access - only specific reporte features
  if (isContabilidad) {
    return ["sales", "customers"].includes(feature);
  }

  // PRODUCCION has restricted access - only Kioscos and Módulos
  if (isProduccion) {
    return ["machines", "modules"].includes(feature);
  }

  // PROVEEDOR has restricted access - Módulos
  if (isProveedor) {
    return ["modules"].includes(feature);
  }

  // PROVEEDOR has restricted access - Módulos
  if (isProgrammer) {
    return ["machines"].includes(feature);
  }

  // Permission rules by menu feature based on the provided matrix
  switch (feature) {
    case "nightscan":
      return isAdmin && (isProduccion || isProyectos || isSat1 || isSat2);

    // Canales
    case "machines":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer &&
          (isGroup || isLicenciaBasica || isLicenciaPro || isPDA || isCash || isManager))
      );
    case "qrs":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "pdas":
      return isAdmin && (isProyectos || isSat1 || isSat2);

    // Gestión
    case "lockersale":
      return isCustomer && (isPDA || isLicenciaBasica || isLicenciaPro || isManager);
    case "pin":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isLicenciaBasica || isLicenciaPro || isManager || isPDA))
      );
    case "discount-codes":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "redeemcodes":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "wristband-redeemcodes":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "rentals":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "calendars":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "email-templates":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "partners":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isLicenciaBasica || isLicenciaPro || isManager))
      );

    // Reporte
    case "pro-dashboard":
      return (
        isCustomer && (isCash || isLicenciaPro || isLicenciaBasica || isGroup || isManager)
      );
    case "dashboard-rental":
      return (
        (isAdmin && (isMaster || isProyectos)) ||
        (isCustomer && (isCash || isLicenciaPro || isGroup || isManager))
      );
    case "operational-dashboard":
      return isAdmin && isMaster;
    case "occupancy-dashboard":
      return isAdmin && isMaster;
    case "sales":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isCash || isGroup || isLicenciaBasica || isLicenciaPro || isManager))
      );
    // Herramienta de integridad de datos, solo admin interno (MASTER ya pasa por el corto arriba).
    case "duplicated-sales":
      return isAdmin && (isProyectos || isSat1 || isSat2);
    case "tiempos":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2))
      );
    case "objectives":
      return isAdmin && isProyectos;
    case "pinrecovers":
      return isAdmin && (isProyectos || isSat1 || isSat2);
    case "localizacion":
      return isAdmin || (isCustomer && (isCash || isPDA || isLicenciaPro || isGroup || isManager));
    case "withdrawals":
      return (
        (isAdmin && isProyectos) ||
        (isCustomer && (isCash || isGroup || isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "activity-dashboard":
      return isAdmin && isMaster;
    case "batteries":
      return isAdmin && (isProyectos || isSat1 || isSat2);

    // Operativa
    case "customers":
      return isAdmin && (isProyectos || isSat1 || isSat2);
    case "contracts":
      return isAdmin && (isProyectos || isSat1 || isSat2);
    case "audit":
      return isAdmin && (isProyectos || isSat1 || isSat2);
    case "tpvs":
      return isAdmin && (isProyectos || isSat2);
    case "modules":
      return isAdmin && (isProyectos || isSat1 || isSat2);
    case "matrixs":
      return isAdmin && isProyectos;
    case "lowbluetooth":
      return isAdmin && isProyectos;
    case "lockersqty":
      return isAdmin && (isProyectos || isSat2);
    case "openings":
      return isAdmin && (isProyectos || isSat2);
    case "updates":
      return isAdmin && (isProyectos || isSat2);
    case "software-versions":
      return isAdmin && (isProyectos || isSat1 || isSat2);

    case "rental-sanity":
      return isAdmin && (isProyectos || isSat1 || isSat2);

    // SAT
    case "supporttickets":
      return isAdmin && isSat1;
    case "incidences":
      return isAdmin && isSat2;
    case "faults":
      return isAdmin && (isSat1 || isSat2);
    case "replacements":
      return isAdmin && isSat2;
    case "slas":
      return isAdmin && (isMaster || isProyectos);
    case "bowler":
      return isAdmin && (isMaster || isProyectos);

    // Baqueira (Special access)
    case "operators":
      return isCustomer && (isBaqueira || !isPDA);
    case "rules":
      return isCustomer && (isBaqueira || !isPDA);
    case "articulos":
      return isCustomer && (isBaqueira || !isPDA);
    case "articles":
      return isCustomer && (isBaqueira || !isPDA);
    case "montarto":
      return isCustomer && (isMontarto || !isPDA);
    case "arqueosbaqueira":
      return isCustomer && (isBaqueira || !isPDA);
    case "ocupationaccumulated":
      return isCustomer && (isBaqueira || !isPDA);
    case "ocupationdaily":
      return isCustomer && (isBaqueira || !isPDA);

    // Soporte
    case "supportticketadd":
      // both customers and SAT1 personnel should be able to open the
      // "Mis Tickets" customer-facing page (SAT1 admins often create on
      // behalf of customers).
      return isCustomer || (isAdmin && isSat1);
    case "support-chat":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer &&
          (isCash || isPDA || isGroup || isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "faqs":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "manuals":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer &&
          (isCash || isPDA || isGroup || isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "api-docs": // legacy alias
    case "integraciones":
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "api":
      // Non-technical API integration page — visible to BASIC and PRO (not locked)
      return (
        (isAdmin && (isProyectos || isSat1 || isSat2)) ||
        (isCustomer && (isGroup || isLicenciaBasica || isLicenciaPro || isManager))
      );
    case "planes":
      // Licence comparison page — visible to all customers
      return isCustomer || isAdmin;

    // Customer self-service user management (PRO only) + Admin access
    case "my-cash-users":
      return (
        (isAdmin && isProyectos) ||
        (isCustomer && (isLicenciaBasica || isLicenciaPro))
      );
    case "my-pda-users":
      return (
        (isAdmin && isProyectos) ||
        (isCustomer && (isLicenciaBasica || isLicenciaPro))
      );
    case "my-manager-users":
      return (
        (isAdmin && isProyectos) ||
        (isCustomer && (isLicenciaBasica || isLicenciaPro))
      );

    // Admin
    case "users":
      return isAdmin && isProyectos;
    case "gameranking":
      return isAdmin && isProyectos;

    // Comercial
    case "sales-modes":
    case "sales-modes-quiz":
    case "proposal-configurator":
    case "proposals":
    case "ar-viewer":
      return isAdmin && isComercial;
      
    case "proposal-prices":
    case "replacement-prices":
      return isAdmin && isMaster;

    default:
      return false;
  }
}

/**
 * Check if user has access to ANY feature in a category
 * @param {string} role - User role
 * @param {string} type - User type
 * @param {string} category - User category
 * @param {string[]} features - Array of features to check
 * @returns {boolean}
 */
export function hasAccessToAny(role, type, category, features) {
  return features.some((feature) => canAccess(role, type, category, feature));
}

export default canAccess;
